import { useState, type FormEvent } from 'react';
import type { Translate } from './i18n';
import { tenantLabel } from './tenant';
import { ActionRow, Field } from './ui';

export interface TenantOption {
  id: string;
  name: string;
}

export interface NewMapInput {
  tenantId: string;
  name: string;
  file: File | null;
}

/** A centered modal for creating a map (optionally with its initial master file). */
export function NewMapDialog({
  tenants,
  initialTenantId,
  t,
  onClose,
  onCreate,
}: {
  tenants: TenantOption[];
  initialTenantId?: string;
  t: Translate;
  onClose: () => void;
  onCreate: (input: NewMapInput) => Promise<boolean>;
}) {
  const [tenantId, setTenantId] = useState(initialTenantId ?? tenants[0]?.id ?? '');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenantId || !name.trim()) return;
    setBusy(true);
    setError(false);
    try {
      if (await onCreate({ tenantId, name: name.trim(), file })) onClose();
      else setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('map.new.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <strong>{t('map.new.title')}</strong>
        </div>
        <form className="ui-form" onSubmit={submit}>
          {tenants.length > 1 && (
            <Field label={t('map.new.team')}>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenantLabel(tenant.name, tenant.id)}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label={t('map.new.name')}>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('map.new.placeholder')}
            />
          </Field>
          <Field label={t('map.new.file')} hint={t('map.new.fileHint')}>
            <input
              type="file"
              accept=".gpx,.geojson,.json"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          {error && <p className="warning">{t('map.new.failed')}</p>}
          <ActionRow>
            <button type="submit" className="button primary" disabled={busy || !name.trim()}>
              {t('map.new.create')}
            </button>
            <button type="button" className="button" onClick={onClose}>
              {t('map.new.cancel')}
            </button>
          </ActionRow>
        </form>
      </div>
    </div>
  );
}
